import { createSelector } from 'reselect'
import { filter, values, get, keys, flatMap, includes, groupBy, map, isEmpty } from 'lodash'

import { getCurrentMenuId } from './appState'
import { getSelectedMenusIds } from './config'
import { getCurrentMenuCategories } from './categories'
import { getItemCategories } from './itemCategory'
import { getCartLineItems } from './cart'
import { makeGetServiceChargeLegalText } from './venue'

const getKioskItems = (state) => state.items

// used for Kiosk
export const getItemsForCurrentMenu = createSelector(getCurrentMenuId, getKioskItems, (currentMenuId, items) =>
  get(items, ['byMenuId', currentMenuId], {}),
)

const getModifierItemsForItem = (menuItem, menuItemsFromState) => {
  if (!menuItem.modifierGroups?.length) return []

  return menuItem.modifierGroups.map((group) => ({
    ...group,
    items: group.items?.map((item) => {
      if (!menuItemsFromState.byId[item.uuid]) {
        return item
      }
      return {
        ...menuItemsFromState.byId[item.uuid],
      }
    }),
  }))
}

// used for POS/QS/CFD to populate item modifier data
export const getPOSItemsForCurrentMenu = createSelector(getCurrentMenuId, getKioskItems, (currentMenuId, items) => {
  let menuItems = { ...get(items, ['byMenuId', currentMenuId], {}) }

  menuItems = {
    ...menuItems,
    byId: {
      ...menuItems.byId,
    },
  }

  for (let key in menuItems.byId) {
    if (!menuItems.byId[key].modifierGroups?.length) {
      continue
    }

    menuItems.byId[key] = {
      ...menuItems.byId[key],
      modifierGroups: getModifierItemsForItem(menuItems.byId[key], menuItems),
    }
  }

  return menuItems
})

export const getItemForCurrentMenuById = createSelector([getItemsForCurrentMenu, (_, id) => id], (currentMenuItems, id) => {
  const item = currentMenuItems?.byId?.[id] ?? {}

  if (!item.modifierGroups?.length) {
    return item
  }

  return {
    ...item,
    modifierGroups: getModifierItemsForItem(item, currentMenuItems),
  }
})

const getItemsValuesForCurrentMenu = createSelector(getPOSItemsForCurrentMenu, (currentMenuItems) =>
  values(get(currentMenuItems, 'byId', {})),
)

export const getCurrentItemValues = createSelector(getItemsForCurrentMenu, (currentMenuItems) =>
  Object.values(currentMenuItems?.byId ?? {}),
)

const getItemsIdsForCurrentMenu = createSelector(getItemsForCurrentMenu, (currentMenuItems) =>
  get(currentMenuItems, 'allIds', []),
)

export const getCurrentMenuItemsFilteredByCategory = (state, categoryId) => {
  return filter(getItemsValuesForCurrentMenu(state), (item) => includes(item.itemCategoryUuids, categoryId))
}

export const getItemsImages = createSelector(getKioskItems, getSelectedMenusIds, (items, selectedMenuIds) =>
  flatMap(selectedMenuIds, (id) => {
    const menuItems = values(get(items, ['byMenuId', id, 'byId']))
    return menuItems.map((item) => get(item, 'images.detail')).filter((image) => !!image)
  }),
)

export const getAreItemsLoaded = createSelector(getKioskItems, getSelectedMenusIds, (items, selectedIds) => {
  const itemKeys = keys(items.byMenuId)
  return selectedIds.every((id) => itemKeys.includes(id))
})

export const getItems = (state) => {
  const itemIds = getItemsIdsForCurrentMenu(state)
  const getItem = makeGetItemWithCartQuantity()
  const categories = get(getCurrentMenuCategories(state), 'byId', {})
  const items = itemIds.map((itemId) => {
    let item = getItem(state, itemId)
    item = {
      ...item,
      color: get(categories, `${item.itemCategoryUuid}.color`, null),
    }

    return item
  })

  return items
}

/**
 * @todo This selector should be refactored to use useSelector
 */
export const getItemsWithQuantity = (state) => {
  const itemIds = getItemsIdsForCurrentMenu(state)
  const categories = getItemCategories(state)

  const items = itemIds.map((itemId) => {
    // we shouldn't be calling the cart with every item, call cart once and then filter
    let item = getItemWithCartQuantity(state, itemId)
    let price = `$${(item.defaultPriceInCents / 100).toFixed(2)}` || '--'
    const status = (item.variants && item.variants[0] && item.variants[0].inventoryState) || 'available'

    if (item.inventory <= 10) {
      price = `${price} - ${item.inventory} LEFT!`
    }

    if (item.cartQuantity === item.orderMaxAmount) {
      price = 'MAX REACHED'
    }

    if (status !== 'available') {
      price = 'SOLD OUT'
    }

    item = {
      ...item,
      color: get(categories, `${item.itemCategoryUuid}.color`, null),
      price,
      categoryId: item.itemCategoryUuid || 'n/a',
      status,
      orderMaxAmount: parseInt(item.orderMaxAmount ?? 999),
      orderMinCount: parseInt(item.orderMinCount ?? 1),
      inventory: item.inventory || 100,
      maxReached: item.cartQuantity === item.orderMaxAmount,
    }

    return item
  })

  const multiCategoryItems = flatMap(items, (item) =>
    (item.itemCategoryUuids.length > 0 ? item.itemCategoryUuids : [null]).map((categoryId) => {
      return { ...item, itemCategoryUuid: categoryId, color: get(categories, `${categoryId}.color`, null) }
    }),
  )

  const groups = groupBy(multiCategoryItems, 'itemCategoryUuid')
  const sortedItems = flatMap(
    map(categories, (category) => category.itemCategoryUuid).concat(null),
    (categoryId) => groups[categoryId],
  )

  return filter(sortedItems, ['modifier', false])
}

export const getMenuItems = createSelector(getItems, (items) => filter(items, ['modifier', false]))

// Return : An object containing the service charge rate; whether the service charge is taxable;
// the name of the service charge; and the legal text for the service charge.
export const getMenuServiceCharge = createSelector(
  [getItemsForCurrentMenu, makeGetServiceChargeLegalText()],
  (currentMenuItems, serviceChargeLegalText) => {
    const serviceFeeItems = get(currentMenuItems, 'serviceFeeItems', [])

    // Favor taxable service charges over non-taxable service charges.
    let service_charges = filter(serviceFeeItems, ['specialType', 'taxable_service_charge'])

    if (service_charges && !isEmpty(service_charges)) {
      return {
        name: get(service_charges, '[0].name') || 'Service Charge',
        rate: get(service_charges, '[0].rate', 0),
        priceInCents: get(service_charges, '[0].variants[0].priceInCents', undefined),
        taxable: true,
        legal: serviceChargeLegalText,
      }
    }

    service_charges = filter(serviceFeeItems, ['specialType', 'service_charge'])

    if (service_charges && !isEmpty(service_charges)) {
      return {
        name: get(service_charges, '[0].name') || 'Service Charge',
        rate: get(service_charges, '[0].rate', 0),
        priceInCents: get(service_charges, '[0].variants[0].priceInCents', undefined),
        taxable: false,
        legal: serviceChargeLegalText,
      }
    }

    return { name: 'Service Charge', rate: 0, taxable: false, legal: null }
  },
)

export const makeGetMenuServiceCharge = () => createSelector(getMenuServiceCharge, (menuItems) => menuItems)

// this is causing the menu screen to re-render on every item change
// the cart should have a summary of the items and display quantities
const getItemWithCartQuantity = (state, id) => {
  const item = get(getItemsForCurrentMenu(state), ['byId', id], {})
  const cartItems = getCartLineItems(state)

  let cartQuantity = 0
  let cartQuantityWithModifiers = 0
  let cartIndex = -1

  const hasRequiredModifiers = item.modifierGroups?.find(
    ({ requiresSelection, min, defaults }) => requiresSelection || min > 0 || defaults?.length,
  )

  for (let i = 0; i < cartItems.length; i++) {
    if (cartItems[i].itemId !== id) continue

    if (hasRequiredModifiers) {
      cartQuantityWithModifiers += cartItems[i].quantity
      continue
    }

    const hasNotesOrModifiers = cartItems[i].notes?.length || cartItems[i].modifiers?.length

    if (hasNotesOrModifiers) {
      cartQuantityWithModifiers += cartItems[i].quantity
      continue
    }

    cartIndex = i
    cartQuantity += cartItems[i].quantity
  }

  return { ...item, cartQuantity, cartQuantityWithModifiers, hasRequiredModifiers, cartIndex }
}

export const makeGetItemWithCartQuantity = () => createSelector([getItemWithCartQuantity], (item) => item)
